import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  MdAdd,
  MdCall,
  MdCheck,
  MdClose,
  MdDoNotDisturbOn,
  MdNavigation,
  MdPhotoCamera,
  MdPhotoLibrary,
  MdRemove,
  MdTimelapse,
} from "react-icons/md";

import CircularLoader from "../../../../../../../Components/CircularLoader/CircularLoader";
import {
  uploadLabel,
  uploadProgress,
} from "../../../../../../../helpers/uploads";
import usePickedFiles from "../../../../../../../hooks/usePickedFiles";
import {
  createOrderNotes,
  fetchSingleOrder,
  storeOrderChange,
} from "../../../../../../../services/orders.services";
import OrderNotes from "./OrderNotes";
import SheetChip, { LINE_STATES } from "./SheetChip";
import LineNotes from "./OrderLineNotes";
import { SpotLine } from "./StopParts";
import StopPhotos from "./StopPhotos";
import {
  ATTENTION_STATUS,
  buildNoteForm,
  buildStatusForm,
  CANT_DELIVER_REASONS,
  deliveredCount,
  deliveryNote,
  FULFILLED_STATUS,
  isCashOrder,
  orderedCount,
  photosNote,
  setDelivered,
  setLineNote,
  setPalletQuantity,
  startLines,
  statusName,
  togglePallet,
} from "./deliverySave";
import {
  deliveryAddress,
  lineStates,
  loadTotals,
  navigateUrl,
  productName,
  totalsParts,
  unweighedNote,
} from "./routeSheet";
import { FULFILLED_OUTSIDE_LABEL } from "./runs";

// Touch sizes (40px and up) on phones and tablets, and on touch screens of
// any size; the compact desktop sizes from lg up with a mouse.
const CARD =
  "overflow-hidden rounded-[9px] border-[1px] border-ui-border bg-ui-surface";
const ACTION =
  "flex min-h-[44px] items-center gap-[6px] rounded-[7px] border-[1px] px-[12px] py-[7px] text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 lg:min-h-0 [@media(pointer:coarse)]:min-h-[44px]";
// 16px on phones: typing in a field doesn't zoom the page on iPhones.
const FIELD =
  "min-h-[40px] w-full rounded-[6px] border-[1px] border-ui-border-strong bg-ui-surface px-[9px] py-[6px] text-[16px] text-ui-text focus:border-brand focus:outline-none sm:text-[12.5px] lg:min-h-0 [@media(pointer:coarse)]:min-h-[40px]";
const LABEL =
  "text-[10px] font-semibold uppercase tracking-[0.06em] text-ui-text-muted";
const STEP =
  "grid h-[40px] w-[40px] shrink-0 place-items-center rounded-[7px] border-[1px] border-ui-border-strong bg-ui-surface text-ui-text transition-colors hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-ui-border-strong disabled:hover:text-ui-text lg:h-[30px] lg:w-[30px] [@media(pointer:coarse)]:h-[40px] [@media(pointer:coarse)]:w-[40px]";
const SMALL_BTN =
  "min-h-[40px] rounded-[6px] border-[1px] border-ui-border bg-ui-surface px-[10px] py-[4px] text-[12px] font-semibold text-ui-text-muted transition-colors hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-ui-border disabled:hover:text-ui-text-muted lg:min-h-[30px] lg:text-[11.5px] [@media(pointer:coarse)]:min-h-[40px]";
// A text button or link with a 40px tap height below lg (and on touch).
const TAP = "min-h-[40px] lg:min-h-0 [@media(pointer:coarse)]:min-h-[40px]";

const CANCELLED_STATUS = 6;

// When a pick was saved, as "13:40" (today) or "Nov 1, 13:40".
const pickedWhen = (at) => {
  const date = at ? new Date(at) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return date.toDateString() === new Date().toDateString()
    ? time
    : `${date.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
};
const pickedBy = (line) => {
  const picks = line?.picks ?? [];
  const last = picks[picks.length - 1];
  if (!last) return "Picked";
  return ["Picked", last.by?.name && `by ${last.by.name}`, pickedWhen(last.at)]
    .filter(Boolean)
    .join(" ");
};

/**
 * The tick box beside an item: picked in Bolt or not. Ticking picks what is
 * left of it; unticking undoes the pick.
 */
const PickTick = ({ name, checked, busy, title, onToggle }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    aria-busy={busy || undefined}
    aria-label={`Picked: ${name}`}
    title={title}
    disabled={busy}
    onClick={onToggle}
    className={`grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[6px] border-[2px] transition-colors disabled:cursor-wait disabled:opacity-50 lg:h-[22px] lg:w-[22px] [@media(pointer:coarse)]:h-[30px] [@media(pointer:coarse)]:w-[30px] ${
      checked
        ? "border-state-success-text bg-state-success-text text-white"
        : "border-ui-border-strong bg-ui-surface text-transparent hover:border-brand"
    }`}
  >
    <MdCheck className="h-[18px] w-[18px] lg:h-[14px] lg:w-[14px]" />
  </button>
);
const customerName = (shipping) =>
  [shipping?.first_name, shipping?.last_name]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");
const telHref = (phone) => `tel:${String(phone).replace(/[^\d+]/g, "")}`;
// A status name as the app's status chips show it ("Pending / New").
const statusLabel = (name) =>
  String(name ?? "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
const lineName = (item) => productName(item) || "-";

// Why Delivered / Partly delivered can't be used on an order: the same rules
// as the Fulfilment dropdown (a location this account may fulfil from, a
// Confirmed order that isn't a return and isn't cancelled).
export const blockedReason = (order, canFulfil) => {
  if (!order?.warehouse_id) return "Set a location on the order first";
  if (!canFulfil(order)) return "You can't fulfil orders at this location";
  if (order?.type === "Return") return "This order is a return";
  if (Number(order?.order_status_id) === CANCELLED_STATUS)
    return "This order is cancelled";
  if (order?.status !== "Confirmed") return "This order isn't confirmed yet";
  return "";
};

const PICK_PHOTOS = `${TAP} flex w-fit cursor-pointer items-center gap-[6px] rounded-[6px] border-[1px] border-ui-border bg-ui-surface px-[10px] py-[6px] text-[12px] font-semibold text-ui-text-muted hover:border-brand hover:text-brand`;

/**
 * Two ways to add photos: "Take photo" opens the camera straight away (on
 * phones and tablets; one photo a tap, each added to the rest), and "Choose
 * photos" picks one or several from the gallery or files at once. `onAdd`
 * gets the FileList. Every photo added goes up together with the save.
 * The inputs' ids start with `id` and are unique to this copy: the driver
 * view shows a stop that the list behind it shows too, and each label must
 * open its own input.
 */
const PhotoPickers = ({ id, onAdd, disabled = false }) => {
  const own = `${id}-${useId().replace(/:/g, "")}`;
  const off = disabled ? "pointer-events-none opacity-40" : "";
  const picked = (event) => {
    onAdd(event.target.files);
    event.target.value = "";
  };
  return (
    <>
      <label
        htmlFor={`${own}-camera`}
        aria-disabled={disabled || undefined}
        className={`${PICK_PHOTOS} hidden [@media(pointer:coarse)]:flex ${off}`}
      >
        <MdPhotoCamera aria-hidden="true" className="h-[15px] w-[15px]" />
        Take photo
      </label>
      <input
        id={`${own}-camera`}
        type="file"
        accept="image/*"
        capture="environment"
        disabled={disabled}
        className="sr-only"
        onChange={picked}
      />
      <label
        htmlFor={own}
        aria-disabled={disabled || undefined}
        className={`${PICK_PHOTOS} ${off}`}
      >
        <MdPhotoLibrary aria-hidden="true" className="h-[15px] w-[15px]" />
        Choose photos
      </label>
      <input
        id={own}
        type="file"
        accept="image/*"
        multiple
        disabled={disabled}
        className="sr-only"
        onChange={picked}
      />
    </>
  );
};

// The photos picked (usePickedFiles items): each is shrunk as soon as it's
// picked, so its preview and name become the shrunk JPEG's. `disabled`
// while they upload: nothing can be added or taken off the save under way.
const Files = ({ items, onAdd, onRemove, id, disabled = false }) => {
  const files = useMemo(() => items.map((item) => item.file), [items]);
  const previews = useMemo(
    () =>
      files.map((file) =>
        /^image\//.test(file.type) && window.URL?.createObjectURL
          ? window.URL.createObjectURL(file)
          : null,
      ),
    [files],
  );
  useEffect(
    () => () =>
      previews.forEach((url) => url && window.URL.revokeObjectURL(url)),
    [previews],
  );
  return (
    <div className="grid gap-[6px]">
      <div className="flex flex-wrap items-center gap-[8px]">
        <PhotoPickers id={id} onAdd={onAdd} disabled={disabled} />
        {files.length > 0 && (
          <span className="text-[12px] text-ui-text-muted lg:text-[11px]">
            {files.length === 1 ? "1 photo" : `${files.length} photos`}: they
            upload together when you save
          </span>
        )}
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-[6px]">
          {files.map((file, index) => (
            <span
              key={items[index].id}
              className="flex items-center gap-[6px] rounded-[6px] border-[1px] border-ui-border px-[6px] py-[3px] text-[12px] lg:text-[11px]"
            >
              {previews[index] && (
                <img
                  src={previews[index]}
                  alt=""
                  className="h-[28px] w-[28px] rounded-[4px] object-cover"
                />
              )}
              {file.name}
              {/* Below lg (and on touch): a 40px tap target round the cross. */}
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                disabled={disabled}
                onClick={() => onRemove(index)}
                className="-my-[6px] grid h-[40px] w-[40px] shrink-0 place-items-center border-0 bg-transparent p-0 text-ui-text-subtle hover:text-state-danger-text lg:my-0 lg:block lg:h-auto lg:w-auto lg:shrink [@media(pointer:coarse)]:-my-[6px] [@media(pointer:coarse)]:grid [@media(pointer:coarse)]:h-[40px] [@media(pointer:coarse)]:w-[40px] [@media(pointer:coarse)]:shrink-0"
              >
                <MdClose className="h-[13px] w-[13px]" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Delivered: Fulfilled, with the pallets each item comes off (pre-picked as
// the status pop-up does; "Change pallets" ticks others and sets quantities).
const DeliveredFields = ({ order, lines, setLines }) => {
  const [open, setOpen] = useState({});
  return (
    <div className="grid gap-[8px]">
      <span className={LABEL}>Items and pallets</span>
      {lines.map((line, index) => {
        const pallets = line?.products?.product_pallets ?? [];
        return (
          <div
            key={line.id}
            className="rounded-[7px] border-[1px] border-ui-border-subtle p-[8px]"
          >
            <div className="flex flex-wrap items-center gap-[8px]">
              <span className="font-semibold">
                {line.item_quantity} × {lineName(line)}
              </span>
              <span className="text-[12px] text-ui-text-muted lg:text-[11.5px]">
                {line.pallet_data?.length
                  ? line.pallet_data
                      .map((data) => {
                        const found = pallets.find(
                          (pallet) => pallet?.pallet?.id === data.pallet_id,
                        );
                        return `${found?.pallet?.name ?? "Pallet"}${
                          found?.pallet?.location?.name
                            ? ` (${found.pallet.location.name})`
                            : ""
                        } × ${data.quantity}`;
                      })
                      .join(", ")
                  : "No pallet chosen"}
              </span>
              {pallets.length > 0 && (
                <button
                  type="button"
                  aria-expanded={Boolean(open[line.id])}
                  onClick={() =>
                    setOpen((current) => ({
                      ...current,
                      [line.id]: !current[line.id],
                    }))
                  }
                  className={`${TAP} ml-auto border-0 bg-transparent p-0 text-[12px] font-semibold text-brand hover:underline lg:text-[11.5px]`}
                >
                  {open[line.id] ? "Done" : "Change pallets"}
                </button>
              )}
            </div>
            <LineNotes item={line} className="mt-[6px]" />
            {open[line.id] && (
              <div className="mt-[6px] grid gap-[4px]">
                {pallets.map((pallet) => {
                  const chosenAt = (line.pallet_data ?? []).findIndex(
                    (data) =>
                      String(data?.pallet_id) === String(pallet?.pallet_id),
                  );
                  const chosen = chosenAt !== -1;
                  return (
                    <div
                      key={pallet.id}
                      className="flex flex-wrap items-center gap-[8px] text-[12px]"
                    >
                      <label className={`${TAP} flex items-center gap-[6px]`}>
                        <input
                          type="checkbox"
                          checked={chosen}
                          onChange={() =>
                            setLines(index, (current) =>
                              togglePallet(current, pallet?.pallet_id),
                            )
                          }
                          className="h-[20px] w-[20px] shrink-0 accent-brand lg:h-[15px] lg:w-[15px] [@media(pointer:coarse)]:h-[20px] [@media(pointer:coarse)]:w-[20px]"
                        />
                        <span>
                          {pallet?.pallet?.name ?? "Pallet"}
                          {pallet?.pallet?.code
                            ? ` · ${pallet.pallet.code}`
                            : ""}
                          {pallet?.pallet?.location?.name
                            ? ` · ${pallet.pallet.location.name}`
                            : ""}
                          {pallet?.product_lot?.name
                            ? ` · Lot ${pallet.product_lot.name}`
                            : ""}{" "}
                          <span className="text-ui-text-muted">
                            (holds {pallet?.quantity})
                          </span>
                        </span>
                      </label>
                      {chosen && (
                        <input
                          type="number"
                          aria-label={`Quantity from ${pallet?.pallet?.name ?? "pallet"}`}
                          value={line.pallet_data[chosenAt].quantity}
                          onChange={(event) =>
                            setLines(index, (current) =>
                              setPalletQuantity(
                                current,
                                chosenAt,
                                event.target.value,
                              ),
                            )
                          }
                          className={`${FIELD} w-[80px] py-[3px]`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {!lines.length && (
        <span className="text-[12px] text-ui-text-muted">
          No products from Bolt on this order.
        </span>
      )}
      {order?.order_details?.some((item) => item.plugin_item_id) && (
        <span className="text-[11px] text-ui-text-subtle">
          Items synced from the marketplace aren't taken off pallets.
        </span>
      )}
    </div>
  );
};

// A line while it's counted: all of it, some still to come, or none yet.
const countState = (count, ordered) =>
  count >= ordered
    ? ["All delivered", "success"]
    : count > 0
      ? [`${ordered - count} left`, "warning"]
      : ["Not delivered", "neutral"];

// Partly delivered: per item, how many the customer got (− / + or All), with
// the total and what's left for a later run above. A note per item opens on
// "Add a note" (or shows the one the item has).
const PartlyFields = ({ lines, setLines, onAll, onReset }) => {
  const [notesOpen, setNotesOpen] = useState([]);
  const [focusNote, setFocusNote] = useState(null);
  useEffect(() => {
    if (focusNote != null) document.getElementById(focusNote)?.focus();
  }, [focusNote]);
  const ordered = lines.reduce((sum, line) => sum + orderedCount(line), 0);
  const delivered = lines.reduce((sum, line) => sum + deliveredCount(line), 0);
  const left = ordered - delivered;

  return (
    <div className="grid gap-[8px]">
      <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[6px]">
        <span className={LABEL}>What was delivered</span>
        {lines.length > 0 && (
          <span aria-live="polite" className="text-[12px] font-semibold">
            {delivered} of {ordered} pcs delivered
            {left > 0 ? ` · ${left} left for a later run` : ""}
          </span>
        )}
        {lines.length > 0 && (
          <div className="flex gap-[6px] sm:ml-auto">
            <button
              type="button"
              onClick={onAll}
              disabled={left === 0}
              className={SMALL_BTN}
            >
              Mark all delivered
            </button>
            <button type="button" onClick={onReset} className={SMALL_BTN}>
              Reset
            </button>
          </div>
        )}
      </div>
      {lines.length > 0 && left === 0 && (
        <span className="rounded-[7px] border-[1px] border-state-warning-border bg-state-warning-bg p-[8px] text-[12px] text-state-warning-text">
          Everything is counted as delivered. Use Delivered instead: it fulfils
          the order and takes the items off their pallets.
        </span>
      )}
      {lines.length > 0 && (
        <ul className="m-[0px] grid list-none gap-[6px] p-[0px]">
          {lines.map((line, index) => {
            const name = lineName(line);
            const quantity = orderedCount(line);
            const count = deliveredCount(line);
            const [state, tone] = countState(count, quantity);
            const change = (next) =>
              setLines(index, (current) => setDelivered(current, next));
            const noteId = `line-note-${line.id}`;
            const noteShown =
              notesOpen.includes(line.id) ||
              Boolean(String(line.fulfillment_note ?? "").trim());
            return (
              <li
                key={line.id}
                className="grid gap-[8px] rounded-[7px] border-[1px] border-ui-border-subtle bg-ui-surface p-[10px]"
              >
                <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[8px]">
                  <div className="grid min-w-[0px] flex-[1_1_220px] gap-[3px]">
                    <span className="font-semibold">{name}</span>
                    <span className="flex flex-wrap items-center gap-[6px] text-[12px] text-ui-text-muted lg:text-[11.5px]">
                      Ordered {quantity}
                      <SheetChip tone={tone}>{state}</SheetChip>
                    </span>
                    <LineNotes item={line} fulfilment={false} />
                  </div>
                  <div
                    role="group"
                    aria-label={`Delivered: ${name}`}
                    className="flex items-center gap-[6px]"
                  >
                    <button
                      type="button"
                      aria-label="One less"
                      disabled={count <= 0}
                      onClick={() => change(count - 1)}
                      className={STEP}
                    >
                      <MdRemove className="h-[16px] w-[16px]" />
                    </button>
                    <span className="min-w-[48px] text-center text-[12px] text-ui-text-muted">
                      <span className="text-[15px] font-bold text-ui-text">
                        {count}
                      </span>{" "}
                      of {quantity}
                    </span>
                    <button
                      type="button"
                      aria-label="One more"
                      disabled={count >= quantity}
                      onClick={() => change(count + 1)}
                      className={STEP}
                    >
                      <MdAdd className="h-[16px] w-[16px]" />
                    </button>
                    <button
                      type="button"
                      disabled={count >= quantity}
                      onClick={() => change(quantity)}
                      className={SMALL_BTN}
                    >
                      All
                    </button>
                  </div>
                </div>
                {noteShown ? (
                  <input
                    id={noteId}
                    type="text"
                    placeholder="Note on this item, e.g. out of stock"
                    aria-label={`Note on ${name}`}
                    value={line.fulfillment_note ?? ""}
                    onChange={(event) =>
                      setLines(index, (current) =>
                        setLineNote(current, event.target.value),
                      )
                    }
                    className={FIELD}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setNotesOpen((open) => [...open, line.id]);
                      setFocusNote(noteId);
                    }}
                    className={`${TAP} justify-self-start border-0 bg-transparent p-[0px] text-[12px] font-semibold text-brand hover:underline lg:text-[11.5px]`}
                  >
                    + Add a note
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {!lines.length && (
        <span className="text-[12px] text-ui-text-muted">
          No products from Bolt on this order.
        </span>
      )}
    </div>
  );
};

/**
 * A stop's photos and notes (StopPhotos) with Take photo / Choose photos:
 * before delivery photos of the picked items (to find them in the van), after
 * it more proof of delivery. The photos are shrunk straight away and shown
 * with a note field; Save adds them all to the order as one comment with its
 * photos, as the order view's comments are saved, so the order's status never
 * changes. `onAdded(note)` gets the saved note. `canAdd` false (while the
 * stop's Delivered / Partly delivered / Can't deliver form is open, which
 * takes its own photos) hides the buttons.
 */
const StopPhotosWithAdd = ({
  order,
  delivered,
  canAdd = true,
  open,
  onToggle,
  onOpen,
  run,
  stop,
  stopCount,
  notify,
  onAdded,
}) => {
  const picked = usePickedFiles();
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [upload, setUpload] = useState({ preparing: false, percent: null });
  const adding = picked.items.length > 0;
  const inputId = `add-photos-${order.id}`;

  const cancel = () => {
    picked.clear();
    setNote("");
  };
  const save = async () => {
    setSaving(true);
    try {
      setUpload({ preparing: picked.preparing, percent: null });
      const files = await picked.ready();
      setUpload({ preparing: false, percent: null });
      const response = await createOrderNotes(
        order.id,
        buildNoteForm(
          photosNote({
            delivered,
            count: files.length,
            run,
            stop,
            stopCount,
            typed: note,
          }),
          files,
        ),
        {
          onUploadProgress: uploadProgress((percent) =>
            setUpload({ preparing: false, percent }),
          ),
        },
      );
      if (response.error) {
        notify.error(
          response.error?.response?.data?.message ||
            "Couldn't add the photos. Try again.",
        );
        return;
      }
      notify.success(
        files.length === 1 ? "Photo added" : `${files.length} photos added`,
      );
      cancel();
      onAdded(response?.data?.result);
    } catch {
      notify.error("Couldn't add the photos. Try again.");
    } finally {
      setSaving(false);
      setUpload({ preparing: false, percent: null });
    }
  };

  return (
    <StopPhotos
      order={order}
      delivered={delivered}
      open={open || adding}
      onToggle={onToggle}
      actions={
        canAdd || adding ? (
          <PhotoPickers
            id={inputId}
            disabled={saving}
            onAdd={(files) => {
              picked.add(files);
              onOpen();
            }}
          />
        ) : null
      }
    >
      {adding && (
        <div className="grid gap-[8px] border-b-[1px] border-ui-border bg-ui-surface-sunken p-[12px]">
          <span className="text-[13px] font-bold text-ui-text">
            {delivered
              ? "Add photos of the delivery"
              : "Add photos of the picked items"}
          </span>
          <Files
            items={picked.items}
            onAdd={picked.add}
            onRemove={picked.remove}
            id={`more-photos-${order.id}`}
            disabled={saving}
          />
          <label className="grid gap-[4px]">
            <span className={LABEL}>Note (optional)</span>
            <textarea
              rows={2}
              value={note}
              placeholder={
                delivered ? "" : "Where they are in the van, e.g. back left"
              }
              onChange={(event) => setNote(event.target.value)}
              className={FIELD}
            />
          </label>
          <div className="flex flex-wrap justify-end gap-[8px]">
            <button
              type="button"
              onClick={cancel}
              disabled={saving}
              className={`${ACTION} flex-1 justify-center border-ui-border bg-ui-surface text-ui-text-muted hover:text-ui-text sm:flex-[0_1_auto]`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className={`${ACTION} flex-1 justify-center border-brand bg-brand text-white hover:bg-brand-hover sm:flex-[0_1_auto]`}
            >
              {saving && <CircularLoader size={12} />}
              {(saving && uploadLabel(upload)) || "Save photos"}
            </button>
          </div>
        </div>
      )}
    </StopPhotos>
  );
};

const ActionPanel = ({
  order,
  kind,
  statuses,
  run,
  stop,
  stopCount,
  canFulfil,
  onFresh,
  onBusy,
  onDone,
  onCancel,
  notify,
}) => {
  // Delivered and Partly delivered start from the order as it is now, as the
  // status pop-up does (it reads the order again each time it opens): the
  // sheet's copy can be stale, e.g. another stop has since taken stock off the
  // same pallet. Can't deliver only adds a comment, so it uses the copy.
  const rereads = kind !== "cant";
  const [latest, setLatest] = useState(rereads ? null : order);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [lines, setAllLines] = useState(() => startLines(order));
  const [note, setNote] = useState("");
  // Photos are shrunk as soon as they're picked (see helpers/uploads). A
  // Can't deliver comment also takes files without an extension, as it did.
  const picked = usePickedFiles({ needsExtension: kind !== "cant" });
  const [upload, setUpload] = useState({ preparing: false, percent: null });
  const [amount, setAmount] = useState(
    order?.amount_received_by_warehouse ?? "",
  );
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const setLines = (index, change) =>
    setAllLines((current) =>
      current.map((line, at) => (at === index ? change(line) : line)),
    );
  useEffect(() => {
    if (!rereads) return undefined;
    let cancelled = false;
    setLoadFailed(false);
    fetchSingleOrder(order.id).then((response) => {
      if (cancelled) return;
      const fresh = response.error ? null : response?.data?.result;
      if (fresh?.id == null) {
        setLoadFailed(true);
        return;
      }
      setLatest(fresh);
      setAllLines(startLines(fresh));
      setAmount(fresh?.amount_received_by_warehouse ?? "");
      onFresh?.(fresh);
    });
    return () => {
      cancelled = true;
    };
    // When the panel opens, and again on Try again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  // What may have changed since the sheet loaded the order: as read when the
  // panel opened, or as the sheet shows it now (a later reload may show it
  // fulfilled elsewhere).
  const changed =
    rereads && latest
      ? [latest, order].some(
          (copy) => Number(copy?.order_status_id) === FULFILLED_STATUS,
        )
        ? "This order is Fulfilled already"
        : blockedReason(latest, canFulfil)
      : "";
  const ready = Boolean(latest) && !changed;
  const cash = kind === "delivered" && ready && isCashOrder(latest);

  const save = async () => {
    setSaving(true);
    onBusy?.(true);
    try {
      // Photos still being shrunk are waited for ("Preparing photos…"); with
      // files, the button then shows how far the upload is.
      setUpload({ preparing: picked.preparing, percent: null });
      const files = await picked.ready();
      setUpload({ preparing: false, percent: null });
      const progress = files.length
        ? [
            {
              onUploadProgress: uploadProgress((percent) =>
                setUpload({ preparing: false, percent }),
              ),
            },
          ]
        : [];
      if (kind === "cant") {
        const response = await createOrderNotes(
          order.id,
          buildNoteForm(
            deliveryNote({ kind, reason, run, stop, stopCount, typed: note }),
            files,
          ),
          ...progress,
        );
        if (response.error) {
          notify.error(
            response.error?.response?.data?.message || "Could not add the note",
          );
          return;
        }
        notify.success("Comment added");
        onDone({ kind, reason });
        return;
      }
      const statusId =
        kind === "delivered" ? FULFILLED_STATUS : ATTENTION_STATUS;
      const name = statusName(statuses, statusId);
      const response = await storeOrderChange(
        buildStatusForm({
          order: latest,
          statusId,
          name,
          lines,
          note: deliveryNote({
            kind,
            run,
            stop,
            stopCount,
            order: latest,
            lines,
            amount: cash ? amount : undefined,
            typed: note,
          }),
          attachments: files,
          amountReceived: cash ? amount : latest?.amount_received_by_warehouse,
        }),
        order.id,
        statusId,
        ...progress,
      );
      if (response.error) {
        notify.error(
          response.error?.response?.data?.message ||
            "Something went wrong try again later",
        );
        return;
      }
      notify.success(response?.data?.message || "Order updated");
      const result = response?.data?.result?.[0];
      onDone({
        kind,
        patch: {
          order_notes: result?.order_notes,
          fullfilment_status: { id: statusId, name },
          order_details: result?.order_details,
          order_status_id: statusId,
        },
      });
    } catch {
      notify.error("Something went wrong try again later");
    } finally {
      setSaving(false);
      setUpload({ preparing: false, percent: null });
      onBusy?.(false);
    }
  };

  const title =
    kind === "delivered"
      ? "Mark as delivered"
      : kind === "partly"
        ? "Mark as partly delivered"
        : "Couldn't deliver";
  const hint =
    kind === "delivered"
      ? "Sets the order to Fulfilled and takes the items off the chosen pallets. A note with what was delivered on this run is added."
      : kind === "partly"
        ? "Count what the customer got. The order goes to Attention / Partially fulfilled, with a note of what was delivered; the rest waits for a later run."
        : "Adds a note with the reason and this run to the order. Its status doesn't change, so it waits for the next run.";
  // Phones get the gist in a line.
  const shortHint =
    kind === "delivered"
      ? "The order becomes Fulfilled."
      : kind === "partly"
        ? "Count what the customer got; the rest waits for a later run."
        : "The order waits for the next run.";
  // Partly delivered with nothing left is a full delivery: Delivered does it.
  const allCounted =
    kind === "partly" &&
    lines.length > 0 &&
    lines.every((line) => deliveredCount(line) >= orderedCount(line));
  const canSave = ready && (kind !== "cant" || Boolean(reason)) && !allCounted;

  return (
    <div className="grid gap-[10px] border-t-[1px] border-ui-border bg-ui-surface-sunken p-[12px]">
      <div className="grid gap-[2px]">
        <span className="text-[13px] font-bold text-ui-text">{title}</span>
        <span className="text-[12px] text-ui-text-muted sm:hidden">
          {shortHint}
        </span>
        <span className="hidden text-[12px] text-ui-text-muted sm:inline lg:text-[11.5px]">
          {hint}
        </span>
      </div>
      {!latest && !loadFailed && (
        <span className="flex items-center gap-[8px] text-[12px] text-ui-text-muted">
          <CircularLoader size={14} />
          Loading the latest order…
        </span>
      )}
      {!latest && loadFailed && (
        <span className="flex flex-wrap items-center gap-[8px] text-[12px] text-state-danger-text">
          Couldn't load the latest order.
          <button
            type="button"
            onClick={() => setAttempt((count) => count + 1)}
            className={`${TAP} border-0 bg-transparent p-0 font-semibold text-brand hover:underline`}
          >
            Try again
          </button>
        </span>
      )}
      {changed && (
        <span className="rounded-[7px] border-[1px] border-state-warning-border bg-state-warning-bg p-[8px] text-[12px] text-state-warning-text">
          {changed}
        </span>
      )}
      {ready && kind === "delivered" && (
        <DeliveredFields order={latest} lines={lines} setLines={setLines} />
      )}
      {ready && kind === "partly" && (
        <PartlyFields
          lines={lines}
          setLines={setLines}
          onAll={() =>
            setAllLines((current) =>
              current.map((line) => setDelivered(line, orderedCount(line))),
            )
          }
          onReset={() => setAllLines(startLines(latest))}
        />
      )}
      {kind === "cant" && (
        <div className="grid gap-[6px]">
          <span className={LABEL}>Why</span>
          <div
            role="radiogroup"
            aria-label="Why"
            className="flex flex-wrap gap-[6px]"
          >
            {CANT_DELIVER_REASONS.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={reason === option}
                onClick={() => setReason(option)}
                className={`${TAP} rounded-full border-[1px] px-[11px] py-[4px] text-[12px] ${
                  reason === option
                    ? "border-brand bg-brand-light font-semibold text-brand"
                    : "border-ui-border text-ui-text-muted hover:border-ui-border-strong"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}
      {cash && (
        <div className="grid gap-[6px] rounded-[7px] border-[1px] border-state-warning-border bg-state-warning-bg p-[8px] text-[12px] text-state-warning-text">
          <span>
            Cash order: receive the pending amount
            {latest?.amount_pending != null
              ? ` (${latest.amount_pending})`
              : ""}
            .
          </span>
          <label className="flex items-center gap-[8px] text-ui-text">
            Received amount
            <input
              type="number"
              value={amount ?? ""}
              onChange={(event) => setAmount(event.target.value)}
              className={`${FIELD} w-[120px] py-[4px]`}
            />
          </label>
        </div>
      )}
      {ready && (
        <label className="grid gap-[4px]">
          <span className={LABEL}>
            {kind === "cant"
              ? "Details (optional)"
              : kind === "partly"
                ? "Note for the order (optional)"
                : "Note (optional)"}
          </span>
          <textarea
            rows={2}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className={FIELD}
          />
        </label>
      )}
      {ready && (
        <Files
          items={picked.items}
          onAdd={picked.add}
          onRemove={picked.remove}
          id={`photo-${order.id}`}
          disabled={saving}
        />
      )}
      {/* Phones: Cancel and Save share the width. */}
      <div className="flex flex-wrap justify-end gap-[8px]">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className={`${ACTION} flex-1 justify-center border-ui-border bg-ui-surface text-ui-text-muted hover:text-ui-text sm:flex-[0_1_auto] sm:justify-start`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !canSave}
          className={`${ACTION} flex-1 justify-center border-brand bg-brand text-white hover:bg-brand-hover sm:flex-[0_1_auto] sm:justify-start`}
        >
          {saving && <CircularLoader size={12} />}
          {(saving && uploadLabel(upload)) ||
            (kind === "delivered"
              ? "Confirm delivered"
              : kind === "partly"
                ? "Save partly delivered"
                : "Save")}
        </button>
      </div>
    </div>
  );
};

/**
 * The delivery list: the route sheet's stops as cards to work through on the
 * road. Each stop: order, customer (Call), address (Navigate), its items and
 * their state, and three actions that save exactly what the warehouse's
 * status pop-up and comments save — Delivered (Fulfilled, with pallets, note,
 * photos and, for cash orders, the amount received), Partly delivered
 * (Attention, per item) and Can't deliver (a comment with the reason). Each
 * saves a note saying what happened on which run (`run`: routeName,
 * startName), with what was typed.
 * `canFulfil(order)` is the account's fulfil permission at the order's
 * location; `onChanged(orderId, patch)` gets the order's new status. While a
 * save runs (`busy`, reported through `onBusy`) every stop's actions wait;
 * `onFresh(order)` gets an order read again before a save.
 * On a run: `onOutcome(orderId, kind, reason)` hears each saved action,
 * `runOutcomes` holds what the run already recorded ({ [orderId]: { kind,
 * reason } }), `spots` where each recorded stop was ({ [orderId]: stopSpot })
 * and `locked` (a sentence) replaces the actions once the run is no longer
 * open. `only` (an order id) shows just that stop's card, numbered as in the
 * list, without the progress line: the driver view's current stop.
 */
const DeliveryList = ({
  stops,
  statuses,
  run = {},
  canFulfil,
  onChanged,
  busy = false,
  onBusy,
  onFresh,
  notify,
  onOutcome,
  runOutcomes = {},
  spots = {},
  locked = "",
  onOpenOrder,
  only = null,
  picks = null,
}) => {
  const [active, setActive] = useState(null);
  const [marked, setOutcomes] = useState({});
  // The stops whose photos and notes are open.
  const [photosOpen, setPhotosOpen] = useState({});
  // The stops as they are now: a photo save that ends after another save on
  // the same stop adds its note to the newest notes, not those it started on.
  const stopsNow = useRef(stops);
  stopsNow.current = stops;
  const outcomes = { ...runOutcomes, ...marked };
  // What the whole list adds up to: pieces, packages and weight.
  const load = useMemo(() => loadTotals(stops), [stops]);
  // The save needs the app's status names (as the status pop-up sends them).
  const ready =
    statusName(statuses, FULFILLED_STATUS) === "FULFILLED" &&
    Boolean(statusName(statuses, ATTENTION_STATUS));

  // On a run, its counts are what the run recorded: an order fulfilled from
  // the order page (or at Attention for another reason) isn't delivered by
  // the run. Fulfilled with nothing recorded on the run is "fulfilled
  // outside this run". Without a run, the orders' statuses say it.
  const onRun = typeof onOutcome === "function";
  const kindOf = (order) => outcomes[order.id]?.kind;
  const fulfilledOutside = (order) =>
    onRun &&
    (kindOf(order) === "fulfilled" ||
      (!kindOf(order) && Number(order?.order_status_id) === FULFILLED_STATUS));
  const delivered = stops.filter((order) =>
    onRun
      ? kindOf(order) === "delivered"
      : Number(order?.order_status_id) === FULFILLED_STATUS,
  ).length;
  const partly = stops.filter((order) =>
    onRun
      ? kindOf(order) === "partly"
      : Number(order?.order_status_id) === ATTENTION_STATUS,
  ).length;
  const outside = stops.filter(fulfilledOutside).length;
  const notDelivered = Object.values(outcomes).filter(
    (outcome) => outcome.kind === "cant",
  ).length;

  return (
    <div className="grid gap-[10px]">
      {only == null && (
        <div className="flex flex-wrap items-center gap-[8px] rounded-[7px] border-[1px] border-ui-border bg-ui-surface-sunken px-[12px] py-[8px]">
          <span
            role="progressbar"
            aria-label="Delivered"
            aria-valuemin={0}
            aria-valuemax={stops.length}
            aria-valuenow={delivered}
            className="block h-[6px] w-[120px] overflow-hidden rounded-full bg-ui-border"
          >
            <span
              className="block h-full rounded-full bg-state-success"
              style={{
                width: `${stops.length ? Math.round((delivered / stops.length) * 100) : 0}%`,
              }}
            />
          </span>
          <span className="text-[12px] text-ui-text-muted lg:text-[11.5px]">
            <b className="text-ui-text">
              {delivered} of {stops.length}
            </b>{" "}
            stops delivered
          </span>
          {partly > 0 && (
            <SheetChip tone="warning">{partly} partly delivered</SheetChip>
          )}
          {notDelivered > 0 && (
            <SheetChip tone="danger">{notDelivered} not delivered</SheetChip>
          )}
          {outside > 0 && (
            <SheetChip tone="neutral">
              {outside} {FULFILLED_OUTSIDE_LABEL.toLowerCase()}
            </SheetChip>
          )}
          {stops.length > 0 && (
            <span
              title={unweighedNote(load) || undefined}
              className="basis-full text-[12px] text-ui-text-muted sm:ml-auto sm:basis-auto lg:text-[11.5px]"
            >
              <b className="text-ui-text">Load:</b>{" "}
              {totalsParts(load).join(" · ")}
            </span>
          )}
        </div>
      )}

      {stops.map((order, index) => {
        if (only != null && order.id !== only) return null;
        const shipping = order?.order_shipping_address_details;
        const address = deliveryAddress(shipping);
        const statusId = Number(order?.order_status_id);
        const status = statusLabel(
          statuses.find((item) => Number(item?.id) === statusId)?.name ??
            order?.fullfilment_status?.name,
        );
        const blocked = ready
          ? blockedReason(order, canFulfil)
          : "Fulfilment statuses are still loading";
        const done = statusId === FULFILLED_STATUS;
        const outcome = outcomes[order.id];
        const lines = lineStates(order, statuses);
        // Tick boxes: items still to pick, once Bolt says what is picked.
        const tickable = (line) =>
          Boolean(picks?.ready) &&
          !done &&
          line.state === "pick" &&
          Boolean(picks.lineOf(line.item?.id));
        const toPick = lines.filter(tickable);
        const pickedCount = toPick.filter((line) =>
          picks.isPicked(line.item?.id),
        ).length;
        const open = active?.orderId === order.id ? active.kind : null;
        const start = (kind) => setActive({ orderId: order.id, kind });
        const stopLoad = loadTotals([order]);
        // Delivered, all or part of it: its photos and notes are the proof.
        const stopDelivered =
          done || ["delivered", "partly"].includes(outcome?.kind);

        return (
          <article
            key={order.id}
            className={CARD}
            aria-label={`Stop ${index + 1}`}
          >
            <div className="grid gap-[8px] p-[12px]">
              <div className="flex flex-wrap items-center gap-[8px]">
                <span className="grid h-[24px] w-[24px] place-items-center rounded-full bg-brand text-[12px] font-bold text-white">
                  {index + 1}
                </span>
                {onOpenOrder ? (
                  <button
                    type="button"
                    aria-label={`Open order #${order.id}`}
                    title="Open the order"
                    onClick={() => onOpenOrder(order)}
                    className={`${TAP} border-0 bg-transparent p-[0px] font-bold text-brand hover:underline print:text-ui-text print:no-underline`}
                  >
                    #{order.id}
                  </button>
                ) : (
                  <span className="font-bold">#{order.id}</span>
                )}
                <span className="text-ui-text-muted">{order?.order_id}</span>
                {order?.self_delivery_tracking?.tracking_id && (
                  <span
                    title="Tracking code"
                    className="font-mono text-[11px] text-ui-text-subtle"
                  >
                    {order.self_delivery_tracking.tracking_id}
                  </span>
                )}
                <OrderNotes order={order} />
                {status && (
                  <SheetChip
                    tone={
                      done
                        ? "success"
                        : statusId === ATTENTION_STATUS
                          ? "warning"
                          : "neutral"
                    }
                  >
                    {done && <MdCheck className="h-[11px] w-[11px]" />}
                    {status}
                  </SheetChip>
                )}
                {outcome?.kind === "cant" && (
                  <SheetChip tone="danger">
                    Not delivered: {outcome.reason}
                  </SheetChip>
                )}
                {fulfilledOutside(order) && (
                  <SheetChip tone="neutral">{FULFILLED_OUTSIDE_LABEL}</SheetChip>
                )}
                {toPick.length > 0 && (
                  <SheetChip
                    tone={pickedCount === toPick.length ? "success" : "neutral"}
                  >
                    {pickedCount === toPick.length && (
                      <MdCheck className="h-[11px] w-[11px]" />
                    )}
                    Picked {pickedCount} of {toPick.length}
                  </SheetChip>
                )}
              </div>
              <SpotLine spot={spots[order.id]} />
              <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[4px] text-[12.5px]">
                <span className="font-semibold">
                  {customerName(shipping) || "-"}
                </span>
                {shipping?.phone && (
                  <a
                    href={telHref(shipping.phone)}
                    className={`${TAP} flex items-center gap-[4px] text-brand hover:underline`}
                  >
                    <MdCall className="h-[13px] w-[13px]" />
                    {shipping.phone}
                  </a>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[4px] text-[12.5px]">
                <span>
                  {address || (
                    <span className="text-state-danger-text">
                      No delivery address
                    </span>
                  )}
                </span>
                {address && (
                  <a
                    href={navigateUrl(address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${TAP} flex items-center gap-[4px] font-semibold text-brand hover:underline`}
                  >
                    <MdNavigation className="h-[13px] w-[13px]" />
                    Navigate
                  </a>
                )}
              </div>
              {lines.length > 0 && (
                <div
                  title={unweighedNote(stopLoad) || undefined}
                  className="flex flex-wrap items-baseline gap-x-[8px] text-[12px] text-ui-text-muted lg:text-[11.5px]"
                >
                  <span className={LABEL}>Items</span>
                  <span>{totalsParts(stopLoad).join(" · ")}</span>
                </div>
              )}
              <div className="grid gap-[3px]">
                {lines.map((line) => {
                  const ticks = tickable(line);
                  const picked = ticks && picks.isPicked(line.item?.id);
                  const [label, tone] = picked
                    ? ["Picked", "success"]
                    : LINE_STATES[line.state];
                  return (
                    <div
                      key={line.item?.id}
                      className="flex flex-wrap items-center gap-x-[6px] gap-y-[4px] text-[12px]"
                    >
                      {ticks && (
                        <PickTick
                          name={lineName(line.item)}
                          checked={picked}
                          busy={picks.isBusy(line.item?.id)}
                          title={
                            picked
                              ? picks.device
                                ? "Picked (saved on this device). Untick to undo."
                                : `${pickedBy(picks.lineOf(line.item?.id))}. Untick to undo.`
                              : "Tick when you've picked it"
                          }
                          onToggle={() => picks.toggle(order, line.item)}
                        />
                      )}
                      <SheetChip tone={tone}>{label}</SheetChip>
                      <span
                        className={
                          line.state === "fulfilled"
                            ? "text-ui-text-muted line-through"
                            : ""
                        }
                      >
                        {line.quantity} × {lineName(line.item)}
                      </span>
                      {line.state === "attention" && line.fulfilled > 0 && (
                        <span className="text-ui-text-muted">
                          · {line.fulfilled} of {line.quantity} delivered
                        </span>
                      )}
                      {line.state !== "fulfilled" && (
                        <LineNotes item={line.item} />
                      )}
                    </div>
                  );
                })}
              </div>
              <StopPhotosWithAdd
                order={order}
                delivered={stopDelivered}
                canAdd={!open}
                open={Boolean(photosOpen[order.id])}
                onToggle={() =>
                  setPhotosOpen((current) => ({
                    ...current,
                    [order.id]: !current[order.id],
                  }))
                }
                onOpen={() =>
                  setPhotosOpen((current) => ({ ...current, [order.id]: true }))
                }
                run={run}
                stop={index + 1}
                stopCount={stops.length}
                notify={notify}
                onAdded={(note) => {
                  const now =
                    stopsNow.current.find((stop) => stop.id === order.id) ??
                    order;
                  onChanged(
                    order.id,
                    note?.id != null
                      ? { order_notes: [...(now?.order_notes ?? []), note] }
                      : {},
                  );
                }}
              />
              {!done && !open && locked && (
                <p className="m-[0px] pt-[2px] text-[12px] text-ui-text-subtle lg:text-[11.5px]">
                  {locked}
                </p>
              )}
              {/* Phones: the actions stretch to fill each row. */}
              {!done && !open && !locked && (
                <div className="flex flex-wrap items-center gap-[8px] pt-[2px]">
                  <button
                    type="button"
                    onClick={() => start("delivered")}
                    disabled={Boolean(blocked) || busy}
                    title={blocked || undefined}
                    className={`${ACTION} flex-[1_1_auto] justify-center border-state-success-border bg-state-success-bg text-state-success-text hover:brightness-95 sm:flex-[0_1_auto] sm:justify-start`}
                  >
                    <MdCheck className="h-[15px] w-[15px]" />
                    Delivered
                  </button>
                  <button
                    type="button"
                    onClick={() => start("partly")}
                    disabled={Boolean(blocked) || busy}
                    title={blocked || undefined}
                    className={`${ACTION} flex-[1_1_auto] justify-center border-state-warning-border bg-state-warning-bg text-state-warning-text hover:brightness-95 sm:flex-[0_1_auto] sm:justify-start`}
                  >
                    <MdTimelapse className="h-[15px] w-[15px]" />
                    Partly delivered
                  </button>
                  <button
                    type="button"
                    onClick={() => start("cant")}
                    disabled={statusId === 6 || busy}
                    className={`${ACTION} flex-[1_1_auto] justify-center border-ui-border bg-ui-surface text-ui-text-muted hover:text-ui-text sm:flex-[0_1_auto] sm:justify-start`}
                  >
                    <MdDoNotDisturbOn className="h-[15px] w-[15px]" />
                    Can't deliver
                  </button>
                  {blocked && (
                    <span className="text-[12px] text-ui-text-subtle lg:text-[11.5px]">
                      {blocked}
                    </span>
                  )}
                </div>
              )}
            </div>
            {open && (
              <ActionPanel
                key={`${order.id}-${open}`}
                order={order}
                kind={open}
                statuses={statuses}
                run={run}
                stop={index + 1}
                stopCount={stops.length}
                canFulfil={canFulfil}
                onFresh={onFresh}
                onBusy={onBusy}
                notify={notify}
                onCancel={() => setActive(null)}
                onDone={(result) => {
                  setActive(null);
                  setOutcomes((current) => ({
                    ...current,
                    [order.id]: result,
                  }));
                  if (result.patch) onChanged(order.id, result.patch);
                  onOutcome?.(order.id, result.kind, result.reason);
                }}
              />
            )}
          </article>
        );
      })}
    </div>
  );
};

export default DeliveryList;
